import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { cn } from "../cn";

export const buttonVariants = cva(
  [
    "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap",
    "tw-cursor-pointer tw-appearance-none tw-border tw-border-solid tw-border-transparent",
    "tw-rounded-lg tw-font-sans tw-text-sm tw-font-medium tw-no-underline tw-transition-all",
    "tw-outline-none focus-visible:tw-border-ring focus-visible:tw-ring focus-visible:tw-ring-ring/50",
    "disabled:tw-pointer-events-none disabled:tw-opacity-50",
    "[&_svg:not([class*='size-'])]:tw-size-4 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0",
  ],
  {
    variants: {
      variant: {
        default: "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary-hover",
        clay: "tw-bg-clay tw-text-foreground hover:tw-bg-clay-hover",
        secondary: "tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary-hover",
        destructive: "tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90",
        outline: "tw-border-border tw-bg-background tw-text-foreground tw-shadow-xs hover:tw-bg-accent hover:tw-text-accent-foreground",
        ghost: "tw-text-foreground hover:tw-bg-ghost-hover hover:tw-text-ghost-foreground",
        link: "tw-text-foreground tw-underline-offset-4 hover:tw-underline",
      },
      size: {
        sm: "tw-h-8 tw-px-3 tw-text-xs has-[>svg]:tw-px-2.5",
        default: "tw-h-9 tw-px-4 tw-py-2 has-[>svg]:tw-px-3",
        lg: "tw-h-10 tw-px-6 has-[>svg]:tw-px-4",
        icon: "tw-size-9 tw-p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean };

// Radix triggers require a DOM ref for positioning when Button renders through Slot.
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({ className, variant = "default", size = "default", asChild = false, type, ...props }, ref) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      // Slot may render a non-button element, which must not receive a button type.
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
